import { useState, useEffect } from 'react';
import { adminGetAiPrompts, adminUpdateAiPrompt } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';

const STEP_META = {
  '1': {
    label:       'Step 1 — Standardize & Domain',
    description: 'Haiku, no web search — standardize name/address and check training data for domain.',
    model:       'haiku',
    placeholders: ['{{CLIENT_DOMAINS}}', '{{RAW_LINES}}'],
  },
  '2': {
    label:       'Step 2 — Sonnet Knowledge',
    description: 'Sonnet, no web search — Fortune 5000 / major brands lookup.',
    model:       'sonnet',
    placeholders: ['{{CLIENT_DOMAINS}}', '{{RAW_LINES}}', '{{STD_LINES}}'],
  },
  '3': {
    label:       'Step 3 — Haiku + Web Search',
    description: 'Haiku with 2 web searches — for unknown companies.',
    model:       'haiku',
    placeholders: ['{{CLIENT_DOMAINS}}', '{{RAW_LINES}}', '{{STD_LINES}}', '{{STEP2_WHAT_THEY_DO}}'],
  },
  '4': {
    label:       'Step 4 — Sonnet + Web Search',
    description: 'Sonnet with 1 web search — final synthesis / fallback.',
    model:       'sonnet',
    placeholders: ['{{CLIENT_DOMAINS}}', '{{RAW_LINES}}', '{{STD_LINES}}', '{{STEP3_DOMAIN}}', '{{STEP3_CONFIDENCE}}', '{{STEP3_WHAT_THEY_DO}}', '{{STEP3_SEARCH_QUERIES}}'],
  },
  'qc_domain': {
    label:       'QC — Domain Verify',
    description: 'Quality control pass to verify and correct domain assignments.',
    model:       'haiku',
    placeholders: ['{{DOMAIN}}', '{{VENDOR_NAME}}', '{{CITY}}', '{{STATE}}', '{{WHAT_THEY_DO}}'],
  },
  'qc_naics': {
    label:       'QC — NAICS Fix',
    description: 'Quality control pass to verify and correct NAICS codes.',
    model:       'haiku',
    placeholders: ['{{DOMAIN}}', '{{VENDOR_NAME}}', '{{WHAT_THEY_DO}}', '{{CITY}}', '{{STATE}}'],
  },
};

const STEP_ORDER = ['1', '2', '3', '4', 'qc_domain', 'qc_naics'];

function ModelBadge({ model }) {
  const isHaiku  = (model || '').toLowerCase().includes('haiku');
  const isSonnet = (model || '').toLowerCase().includes('sonnet');
  const bg    = isHaiku  ? 'rgba(16,185,129,0.1)'  : isSonnet ? 'rgba(99,102,241,0.1)' : 'rgba(100,116,139,0.1)';
  const color = isHaiku  ? '#059669'                : isSonnet ? '#4f46e5'               : '#475569';
  const label = isHaiku  ? 'haiku'                  : isSonnet ? 'sonnet'                : model || '—';
  return (
    <span style={{ background: bg, color, borderRadius: 4, fontSize: 9, fontWeight: 700, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
      {label}
    </span>
  );
}

const FLOW_STEPS = [
  { type: 'sql',  label: 'Load tenant domains + suppressed email domains from DB' },
  { type: 'sql',  label: 'Build raw data block — strip suppressed email domains from vendor row' },
  { type: 'sql',  label: 'Step 0 — Jina pre-check: if website_domain present, fetch page to confirm it belongs to this company. Exit here if confirmed (confidence 95).' },
  { type: 'ai',   label: 'Step 1 — Haiku (no search)', sub: 'Standardize name + address. Detect is_person. Domain lookup from training data only. Returns routing hint: confirmed / sonnet_knowledge / search_needed.' },
  { type: 'jina', label: 'Jina verify (inline): if Step 1 finds a domain, fetch the page to confirm before trusting it.' },
  { type: 'ai',   label: 'Step 2 — Sonnet (no search)', sub: 'Only runs if Step 1 routing = sonnet_knowledge. Fortune 5000 / major global brands. Returns domain + full profile from training data.' },
  { type: 'jina', label: 'Jina verify (inline): if Step 2 finds a domain with confidence 70–89, confirm via page fetch. Confidence ≥ 90 skips Jina (major sites block scrapers).' },
  { type: 'ai',   label: 'Step 3 — Haiku + 2 web searches', sub: 'Only runs if Steps 1–2 did not confirm. Unknown/smaller companies. Sets needs_sonnet=true if still uncertain after 2 searches.' },
  { type: 'jina', label: 'Jina verify (inline): if Step 3 finds a domain that looks unrelated to the vendor name, confirm via page fetch before trusting.' },
  { type: 'ai',   label: 'Step 4 — Sonnet + 1 web search', sub: 'Only runs if Step 3 needs_sonnet=true or domain confidence < 70. Receives Step 3 search queries + results as context. Final fallback.' },
  { type: 'ai',   label: 'QC — Domain Verify', sub: 'Runs after any step that found a domain where vendor name and domain look unrelated. Re-checks: confirms, replaces, or nulls the domain.' },
  { type: 'ai',   label: 'QC — NAICS Fix', sub: 'Runs if the model returned a non-6-digit NAICS code that normalisation rejected. Re-queries with company context to get a valid code.' },
  { type: 'sql',  label: 'is_internal check: if resolved domain matches a tenant domain in DB → set is_internal = true on the result.' },
  { type: 'sql',  label: 'Upsert master_records: best-wins — domain/NAICS/profile only overwritten when new confidence ≥ existing.' },
  { type: 'sql',  label: 'Upsert master_addresses: standardized address + phone/email written per address seq.' },
  { type: 'sql',  label: 'Upsert job_row_results: full enrichment output saved for per-job results and audit views.' },
];

const TYPE_STYLE = {
  sql:  { bg: '#f0f9ff', border: '#bae6fd', dot: '#0369a1', label: 'SQL' },
  ai:   { bg: '#fdf4ff', border: '#e9d5ff', dot: '#7c3aed', label: 'AI'  },
  jina: { bg: '#f0fdf4', border: '#bbf7d0', dot: '#16a34a', label: 'Jina' },
};

function PipelineFlow() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 28 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Complete Enrichment Pipeline</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>SQL pre/post steps + all AI steps in order</span>
        </div>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{open ? '▲ hide' : '▼ show'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 18px 18px', borderTop: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', gap: 16, padding: '10px 0 14px', flexWrap: 'wrap' }}>
            {Object.entries(TYPE_STYLE).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: v.dot }} />
                <span style={{ fontSize: 11, color: '#64748b' }}>{v.label}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {FLOW_STEPS.map((step, i) => {
              const ts = TYPE_STYLE[step.type];
              return (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4, flexShrink: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: ts.dot }} />
                    {i < FLOW_STEPS.length - 1 && <div style={{ width: 1, height: 20, background: '#e2e8f0', marginTop: 2 }} />}
                  </div>
                  <div style={{ background: ts.bg, border: `1px solid ${ts.border}`, borderRadius: 6, padding: '6px 10px', flex: 1, marginBottom: 2 }}>
                    <div style={{ fontSize: 12, color: NAVY, fontWeight: 500, lineHeight: 1.4 }}>{step.label}</div>
                    {step.sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, lineHeight: 1.4 }}>{step.sub}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AiEngine() {
  const [prompts,   setPrompts]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selected,  setSelected]  = useState(null); // step key string
  const [editing,   setEditing]   = useState(null); // { system_text, user_template, model }
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved,     setSaved]     = useState(false);

  useEffect(() => {
    adminGetAiPrompts()
      .then(res => {
        setPrompts(res.prompts || []);
        setLoading(false);
      })
      .catch(e => {
        setLoadError(e.message);
        setLoading(false);
      });
  }, []);

  function promptByStep(step) {
    return prompts.find(p => String(p.step) === String(step)) || null;
  }

  function selectStep(step) {
    const p = promptByStep(step);
    setSelected(step);
    setEditing(p ? { system_text: p.system_text || '', user_template: p.user_template || '', model: p.model || '' } : null);
    setSaveError('');
    setSaved(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!selected || !editing) return;
    setSaving(true); setSaveError(''); setSaved(false);
    try {
      const res = await adminUpdateAiPrompt(selected, {
        system_text:   editing.system_text,
        user_template: editing.user_template,
        model:         editing.model,
      });
      // Update local prompts list
      setPrompts(ps => ps.map(p =>
        String(p.step) === String(selected)
          ? { ...p, ...res.prompt }
          : p
      ));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const meta = selected ? (STEP_META[selected] || {}) : {};

  return (
    <div style={s.page}>
      <AdminSidebar active="ai-engine" />
      <main style={s.main}>
        <div style={s.header}>
          <h1 style={s.title}>AI Engine</h1>
          <p style={s.sub}>Edit enrichment prompts used by the 4-step Claude pipeline</p>
        </div>

        <PipelineFlow />

        {loading   && <div style={{ color: '#64748b', padding: 32 }}>Loading prompts…</div>}
        {loadError && <div style={{ color: '#ef4444', padding: 32 }}>{loadError}</div>}

        {!loading && !loadError && (
          <div style={s.layout}>
            {/* Left panel — step list */}
            <div style={s.list}>
              <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Pipeline Steps
                </div>
              </div>
              {STEP_ORDER.map(step => {
                const p    = promptByStep(step);
                const m    = STEP_META[step] || {};
                const isActive = selected === step;
                return (
                  <div
                    key={step}
                    style={{
                      ...s.listItem,
                      background:  isActive ? 'rgba(243,116,40,0.06)' : '#fff',
                      borderLeft:  isActive ? `3px solid ${ORANGE}`   : '3px solid transparent',
                    }}
                    onClick={() => selectStep(step)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 3 }}>
                      <span style={{ color: NAVY, fontWeight: 600, fontSize: 12, flex: 1, lineHeight: 1.3 }}>{m.label || step}</span>
                      <ModelBadge model={p?.model || m.model} />
                    </div>
                    {p?.updated_at && (
                      <div style={{ color: '#cbd5e1', fontSize: 10 }}>
                        {new Date(p.updated_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Right panel — editor */}
            <div style={s.editor}>
              {selected && editing ? (
                <form onSubmit={handleSave}>
                  {/* Editor header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div>
                      <h2 style={{ color: NAVY, fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>{meta.label || selected}</h2>
                      <p style={{ color: '#64748b', fontSize: 12, margin: 0 }}>{meta.description || ''}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, marginLeft: 16 }}>
                      {saved && <span style={{ color: '#059669', fontSize: 12, fontWeight: 500 }}>Saved!</span>}
                      <button type="submit" style={s.btnPrimary} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Changes'}
                      </button>
                    </div>
                  </div>

                  {saveError && <div style={s.errorBox}>{saveError}</div>}

                  {/* Model field */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={s.label}>Model</label>
                    <input
                      style={{ ...s.input, fontFamily: 'monospace', fontSize: 13 }}
                      value={editing.model || ''}
                      onChange={e => setEditing(ed => ({ ...ed, model: e.target.value }))}
                      placeholder="e.g. claude-3-haiku-20240307"
                    />
                  </div>

                  {/* System Prompt */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={s.label}>System Prompt</label>
                    <textarea
                      style={{ ...s.textarea, minHeight: 80 }}
                      value={editing.system_text || ''}
                      onChange={e => setEditing(ed => ({ ...ed, system_text: e.target.value }))}
                      spellCheck={false}
                      rows={3}
                    />
                  </div>

                  {/* User Template */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={s.label}>User Template</label>
                    <textarea
                      style={{ ...s.textarea, minHeight: 400, resize: 'vertical' }}
                      value={editing.user_template || ''}
                      onChange={e => setEditing(ed => ({ ...ed, user_template: e.target.value }))}
                      spellCheck={false}
                      rows={20}
                    />
                  </div>

                  {/* Placeholders */}
                  {meta.placeholders && meta.placeholders.length > 0 && (
                    <div style={s.placeholderBox}>
                      <span style={{ fontWeight: 600, color: '#475569', marginRight: 8, fontSize: 11 }}>Available placeholders:</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                        {meta.placeholders.map(ph => (
                          <code key={ph} style={s.phChip}>{ph}</code>
                        ))}
                      </div>
                    </div>
                  )}
                </form>
              ) : (
                <div style={{ color: '#94a3b8', fontSize: 13, padding: 32, textAlign: 'center' }}>
                  Select a pipeline step on the left to edit its prompt.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page:           { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:           { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:         { marginBottom: 28 },
  title:          { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  sub:            { color: '#64748b', fontSize: 13, margin: '4px 0 0' },
  layout:         { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' },
  list:           { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  listItem:       { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' },
  editor:         { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  label:          { display: 'block', color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  input:          { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, fontSize: 14, padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  textarea:       { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, fontSize: 12, padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: "'Fira Mono', 'Consolas', 'Monaco', monospace", lineHeight: 1.6, display: 'block' },
  placeholderBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#475569' },
  phChip:         { background: 'rgba(10,37,64,0.07)', color: NAVY, borderRadius: 4, padding: '2px 7px', fontSize: 11, fontFamily: 'monospace' },
  errorBox:       { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '10px 14px', marginBottom: 16 },
  btnPrimary:     { background: ORANGE, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '9px 18px' },
};
